import {
  fetchDatabaseAiTeammate,
  iterateDatabaseAiTeammateDocumentParts
} from '@databases/ai-teammate-client';
import { logError } from '@databases/logs';
import { StreamingCsvParser, TokenType } from '@databases/streaming-csv-parser';
import { parseTextBasedFromHtml } from '@databases/utils/database/field-types/text';
import { parseHTML } from '@databases/utils/lib/dom';

import { toMarkdown } from '@canvas/adf/to-markdown';

import { readDatabaseContent } from './handlers/read-database-content-handler';

export type PreviewDatabaseAiFieldOptions = {
  contentId: string;
  entryId: string;
  fieldName: string;
  locale: string;
  prompt: string;
  abortSignal?: AbortSignal;
};

function formatPreviewValue(value: string): string {
  const html = parseHTML(value);
  if (html.children().length === 0) {
    return value;
  }

  const parsedValue = parseTextBasedFromHtml(html);
  return parsedValue.rich ? toMarkdown(parsedValue.rich).trimEnd() : parsedValue.plain;
}

export async function previewDatabaseAiField({
  contentId,
  entryId,
  fieldName,
  locale,
  prompt,
  abortSignal
}: PreviewDatabaseAiFieldOptions): Promise<string | null> {
  const content = readDatabaseContent(contentId, {
    includeSelection: false,
    populateStreamMappings: false,
    entryScope: { kind: 'specific-entries', entryIds: [entryId] }
  });
  if (content.contentType !== 'database') {
    throw new Error('Expected database content for AI field preview');
  }

  const reader = await fetchDatabaseAiTeammate(
    {
      prompt,
      locale,
      databaseContext: {
        content_id: contentId,
        title: content.title,
        url: content.url,
        csv: content.csv
      }
    },
    abortSignal
  );
  let summary: string | null = null;
  const parser = new StreamingCsvParser(
    (token) => {
      if (
        token.tokenType === TokenType.VALUE &&
        token.fieldName === fieldName &&
        typeof token.value === 'string'
      ) {
        summary = formatPreviewValue(token.value);
      }
    },
    'edit',
    {
      dataValueStrategy: {
        kind: 'single-target',
        targetFieldName: fieldName
      }
    }
  );

  try {
    for await (const documentPart of iterateDatabaseAiTeammateDocumentParts(reader)) {
      parser.onReceiveChunk(documentPart);
    }
    parser.onReceiveChunk('\n');

    const errors = parser.errorCollector.getErrors();
    if (errors.length > 0) {
      throw new Error(errors.map(({ message }) => message).join('; '));
    }

    return summary;
  } catch (error) {
    logError(
      'Failed to generate database AI field preview',
      error instanceof Error ? error : new Error(String(error))
    );

    try {
      await reader.cancel();
    } catch (cancelError) {
      logError(
        'Failed to cancel database AI field preview stream',
        cancelError instanceof Error ? cancelError : new Error(String(cancelError))
      );
    }

    throw error;
  }
}
